import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import RecoverPasswordProvider from "./RecoverPasswordProvider";
import UtilityHelper from "../../../../service/utilities/UtilityHelper";
import { phoneMobileDefault } from "../../../../const/MobileConstant";
import { CountryCodeList } from "../../../../model/country_code/CountryCodeModel";
import { navigate } from "../../../../service/navigation/Navigation";
import { appRoutes } from "../../../../routes/routes";
import SessionStorage from "../../../../service/storage/SessionStorage";
import StorageKeys from "../../../../const/StorageKeys";

type RecoverPasswordTypes = {
  emailField: string;
  phoneField: string;
  phoneCodeField: string;
  isFormValid: boolean;
  processing: boolean;
  verificationType: "0" | "1";
  flag: "0" | "1";

  handleOnChange: (
    name: string,
    value: string,
    isValid: boolean | undefined
  ) => void;
  submitDetail: () => void;
  handleChangeFlagType: (id: "0" | "1") => void;
  applyCountryCode: (countryCodeDetail: CountryCodeList) => void;
};

const RecoverPasswordContext = createContext<RecoverPasswordTypes | undefined>(
  undefined
);

export const RecoverPasswordContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const verificationTypeSession: string =
    SessionStorage.getValue(StorageKeys.verification_type) || "email";

  const verificationTypeSelected: "email" | "phone" = ![
    "email",
    "phone",
  ].includes(verificationTypeSession)
    ? "email"
    : (verificationTypeSession as "email" | "phone"); // 0-email, 1-mobile

  const verificationType: "0" | "1" =
    verificationTypeSelected === "phone" ? "1" : "0"; // 0-email, 1-email/mobile

  const [processing, setProcessing] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const [flag, setFlag] = useState<"0" | "1">("0"); // 0-email, 1-mobile

  const [emailField, setEmailField] = useState<string>("");

  const [phoneField, setPhoneField] = useState<string>("");
  const [phoneCodeField, setPhonCodeField] = useState<string>(
    phoneMobileDefault.phone_code
  );
  const [phoneCodeIdField, setPhoneCodeIdField] = useState<string>(
    phoneMobileDefault.phone_code_id
  );

  const resetFields = () => {
    setIsFormValid(false);
    setEmailField("");

    setPhoneCodeIdField(phoneMobileDefault.phone_code_id);
    setPhonCodeField(phoneMobileDefault.phone_code);
    setPhoneField("");

    setFlag("0");
  };

  const handleChangeFlagType = (id: "0" | "1") => {
    resetFields();
    setFlag(id);
  };

  const handleOnChange = (fieldName: string, fieldValue: string) => {
    fieldName = fieldName.toLowerCase();

    if (fieldName === "email") {
      setEmailField(fieldValue);
    } else if (fieldName === "phone_number") {
      setPhoneField(fieldValue);
    }
  };

  const applyCountryCode = (countryCodeDetail: CountryCodeList) => {
    if (countryCodeDetail != null) {
      setPhoneCodeIdField(countryCodeDetail.country_id!);
      setPhonCodeField(countryCodeDetail.country_phonecode!);
    }
  };

  const submitDetail = async () => {
    let isValid: boolean = false;

    if (flag === "0") {
      if (
        UtilityHelper.isNotEmpty(emailField) &&
        UtilityHelper.isEmail(emailField)
      ) {
        isValid = true;
      }
    } else if (flag === "1") {
      if (
        UtilityHelper.isNotEmpty(phoneField) &&
        UtilityHelper.phoneNumberValid(phoneField) &&
        UtilityHelper.isNotEmpty(phoneCodeIdField)
      ) {
        isValid = true;
      }
    }

    setIsFormValid(!isValid);

    if (isValid) {
      setProcessing(true);
      const statusResponse: boolean =
        await RecoverPasswordProvider.submitSendAPI({
          flag: UtilityHelper.isNotEmpty(emailField) ? "0" : "1",
          emailAddress: emailField,
          mobileCodeId: phoneCodeIdField,
          mobileCode: phoneCodeField,
          mobileNumber: phoneField,
        });

      if (statusResponse) {
        //RecoverPasswordProvider.navigateNewPassword();
        navigate(appRoutes.routeNameForgotPasswordVerification, {
          replace: true,
        });
      }

      setProcessing(false);
    }
  };

  useEffect(() => {
    resetFields();
  }, []);

  return (
    <RecoverPasswordContext.Provider
      value={{
        emailField,
        phoneField,
        phoneCodeField,
        isFormValid,
        processing,
        verificationType,
        flag,
        submitDetail,
        handleOnChange,
        handleChangeFlagType,
        applyCountryCode,
      }}
    >
      {children}
    </RecoverPasswordContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const RecoverPasswordContextImport = () => {
  const context = useContext(RecoverPasswordContext);
  if (!context) {
    throw new Error(
      "RecoverPasswordContextImport must be used within a RecoverPasswordContextProvider"
    );
  }
  return context;
};
